import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict';
import { da, de, enGB, enUS, es, fr, it, type Locale as DateFnLocale, nb, nl, pl, pt, sv } from 'date-fns/locale';
import { parseISO } from 'date-fns/parseISO';
import { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { millisecondsInDay, millisecondsInYear } from './dateConstants';
import { getHoursAndMinutes } from './dateTimeConverters';
import { wrapFormattedPartsUnitInSpan } from './utils';

const localeMap: Record<LanguageRegionCode, DateFnLocale> = {
  'en-US': enUS,
  'fr-FR': fr,
  'de-DE': de,
  'es-ES': es,
  'es-MX': es,
  'en-GB': enGB,
  'it-IT': it,
  'da-DK': da,
  'nl-NL': nl,
  'nb-NO': nb,
  'pl-PL': pl,
  'pt-PT': pt,
  'pt-BR': pt,
  'sv-SE': sv
};

/**
 * Formats time as HH:mm:ss
 * @param totalSeconds Time, in seconds
 * @returns Time formatted as HH:mm:ss
 */
export function formatTimeHhMmSs(totalSeconds: number): string {
  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / (60 * 60));

  const secondsString = String(seconds).padStart(2, '0');
  const minutesString = String(minutes).padStart(2, '0');

  if (hours) {
    return `${hours}:${minutesString}:${secondsString}`;
  }
  return `${minutesString}:${secondsString}`;
}
/**
 * Converts seconds to hours and minutes and wraps the units in spans so they can be styled differently than the numeric values
 * @param intl Intl object
 * @param seconds Seconds value
 * @param abbreviateMultiDay If true, will return "Multi-day" if the hours are greater than or equal to 12
 * @param unitSpace Include a space before the unit
 * @returns A fragment with the converted time values and the units, wrapped in spans
 */
export function getFormattedHoursMinutesWithUnitsInSpans(
  intl: IntlShape,
  seconds: number,
  unitSpace?: boolean,
  abbreviateMultiDay?: boolean
): ReactNode {
  const { hours, minutes } = getHoursAndMinutes(seconds);

  if (hours > 999) {
    return wrapFormattedPartsUnitInSpan(intl.formatNumberToParts(hours, { style: 'unit', unit: 'hour', unitDisplay: 'narrow' }), unitSpace);
  }

  if (hours >= 12 && abbreviateMultiDay) {
    return <span>{intl.formatMessage({ defaultMessage: 'Multi-day' })}</span>;
  }

  if (hours === 0) {
    return intl.formatMessage(
      { defaultMessage: '{minutes}<span>m</span>' },
      { span: (text: ReactNode) => <span key={text?.toString()}>{text}</span>, hours, minutes }
    );
  }

  return intl.formatMessage(
    { defaultMessage: '{hours}<span>h</span> {minutes}<span>m</span>' },
    { span: (text: ReactNode) => <span key={text?.toString()}>{text}</span>, hours, minutes }
  );
}

/**
 * Formats a date string as a relative time ago string. See https://date-fns.org/v4.1.0/docs/formatDistanceToNowStrict for formatting details.
 *
 * Note: [DISCO-20903](https://alltrails.atlassian.net/browse/DISCO-20903) - we may want to switch away from date-fns long term for bundle size benefits.
 * @param timestamp Date string to format
 * @param languageRegionCode Language region code
 * @returns A exact time ago string. If the date is more than a year ago, it will be formatted as (Month, year).
 */
export function humanizeTimeSpan(timestamp: string, languageRegionCode?: LanguageRegionCode): string {
  const date = new Date(timestamp);
  const locale = languageRegionCode || 'en-US';
  // if date is more than a year ago, format it as (Month, year)
  const now = new Date(Date.now());
  const diff = +now - +date;
  if (diff > millisecondsInYear) {
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
  }

  return formatDistanceToNowStrict(date, { addSuffix: true, locale: localeMap[locale] });
}

/**
 * @param date Date or string to format
 * @param languageRegionCode Language region code
 * @param longMonth Whether to use long or shortmonth names (ex: January or Jan)
 * @returns A date string in the format of "January 1, 2021" or "Jan 1, 2021".
 * For non-en-US locales, formatting adapts to the locale. Examples:
 *   - "fr-FR": "9 décembre 2025" or "9 déc. 2025"
 *   - "de-DE": "9. Dezember 2025" or "9. Dez. 2025"
 */
export function monthDayYear(date: Date | string, languageRegionCode: LanguageRegionCode = 'en-US', longMonth = true): string {
  return new Date(date).toLocaleDateString(languageRegionCode, {
    month: longMonth ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Formats a date string for photos in lightboxes. If the date is less than 7 days ago, it will be formatted as a relative time ago string. Otherwise, it will be formatted as a date string.
 * @param timestamp Date string to format
 * @param languageRegionCode Language region code
 * @returns
 */
export function photoTimeStamp(timestamp: string, languageRegionCode: LanguageRegionCode): string {
  const uploadDate: Date = new Date(timestamp);
  const today: Date = new Date(Date.now());
  const dateFormatter = new Intl.DateTimeFormat(languageRegionCode, { year: 'numeric', month: 'short', day: 'numeric' });
  let formattedUploadDate = dateFormatter.format(uploadDate);
  if ((+today - +uploadDate) / millisecondsInDay < 7) {
    formattedUploadDate = humanizeTimeSpan(timestamp.toString(), languageRegionCode);
  }
  return formattedUploadDate;
}

/**
 * Formats a date string as a relative time ago string. See https://date-fns.org/v4.1.0/docs/formatDistanceToNow for formatting details.
 *
 * Note: [DISCO-20903](https://alltrails.atlassian.net/browse/DISCO-20903) - we may want to switch away from date-fns long term for bundle size benefits.
 * @param dateStr Date string to format
 * @param languageRegionCode Language region code
 * @returns A time ago string
 */
export function timeAgo(dateStr: string, languageRegionCode: LanguageRegionCode = 'en-US'): string {
  const date = parseISO(dateStr);
  const options = { addSuffix: true, locale: localeMap[languageRegionCode] };
  return formatDistanceToNow(date, options);
}
